import type { AnnexIISerialReferenceValue } from './AppSettings';
import { ChecklistEvaluationKey } from './ChecklistEvaluations';
import type { EvaluationCoreDescriptor } from './ChecklistEvaluations';
import { FlagPurposeType } from './Flags';
import { matchSerialAgainstAnnexII } from './matchSerialAgainstAnnexII';

const DEFAULT_OEM = 'qcells';

const OEM_LABELS: Record<string, string> = {
  qcells: 'Qcells',
};

const oemLabel = (oem: string): string => OEM_LABELS[oem] ?? oem;

/**
 * `derived`/`entered` are declared numeric by the shared descriptor contract (shared with the
 * scalar tolerance evaluations), but this evaluation's actual values are string/object-shaped —
 * the entered serial and the resolved Annex II reference map. The server/hydrate layer casts at
 * the call site; these helpers narrow back to the real runtime shape here.
 */
interface SerialValidationDerived {
  annexIIReference?: AnnexIISerialReferenceValue;
  panelModel?: string;
}
interface SerialValidationEntered {
  serial?: string | null;
}

export const serialValidationCore: EvaluationCoreDescriptor = {
  key: ChecklistEvaluationKey.serialValidation,
  label: 'Q.TRON Annex II',
  repeatable: false,
  flagPurposeType: FlagPurposeType.rejection,
  supportsDecisionOptions: true,
  defaultParams: { oem: DEFAULT_OEM },
  fieldFormats: {
    panelModel: { label: 'Panel Model' },
    serial: { label: 'Serial Number' },
  },
  verdictMessages: {
    pass: 'Serial matched against Annex II — no flag recommended',
    fail: 'Serial not found in Annex II — rejection flag recommended',
    incomplete: 'Data unavailable',
  },
  computeRow: (derived, entered, params) => {
    const serial = (entered as unknown as SerialValidationEntered).serial;
    if (!serial || !serial.trim()) {
      return { verdict: 'incomplete' };
    }

    const oem = params.oem ?? DEFAULT_OEM;
    const reference = (derived as unknown as SerialValidationDerived).annexIIReference?.[oem];
    if (!reference) {
      return { verdict: 'incomplete' };
    }

    return { verdict: matchSerialAgainstAnnexII(serial, reference) ? 'pass' : 'fail' };
  },
  buildFlagReason: (rows, params) => {
    const row = rows[0];
    const serial = ((row?.entered as unknown as SerialValidationEntered | undefined)?.serial ?? '').trim();
    const oem = params.oem ?? DEFAULT_OEM;

    return `Serial ${serial} not found in ${oemLabel(oem)} Annex II reference`;
  },
};
